import type { DecisionRecord, ProjectState, Scenario } from "./types";
import { applyEffects } from "./effects";
import { resolveEligibleEvents } from "./scheduler";

export function startProject(scenario: Scenario): ProjectState {
  const base: ProjectState = {
    ...scenario.initialMetrics,
    day: 1,
    activeEventIds: [],
    resolvedEventIds: [],
    pendingEvents: [],
    history: [],
  };
  return { ...base, activeEventIds: resolveEligibleEvents(base, scenario) };
}

/** Pure reducer step: (state, eventId, choiceId) -> new state. No store access inside. */
export function advanceProject(
  scenario: Scenario,
  state: ProjectState,
  eventId: string,
  choiceId: string
): ProjectState {
  const event = scenario.events.find((e) => e.id === eventId);
  const choice = event?.choices.find((c) => c.id === choiceId);
  if (!event || !choice) return state;

  const applied = applyEffects(state, choice.effects);

  const record: DecisionRecord = {
    day: state.day,
    eventId: event.id,
    eventTitle: event.title,
    choiceId: choice.id,
    choiceText: choice.text,
    effects: choice.effects,
    resultText: choice.resultText,
  };

  return {
    ...applied,
    activeEventIds: applied.activeEventIds.filter((id) => id !== eventId),
    resolvedEventIds: [...applied.resolvedEventIds, event.id],
    pendingEvents: [...applied.pendingEvents, ...(choice.schedules ?? [])],
    history: [...applied.history, record],
  };
}

export function advanceDay(scenario: Scenario, state: ProjectState): ProjectState {
  const next: ProjectState = { ...state, day: state.day + 1 };
  const eligible = resolveEligibleEvents(next, scenario);
  return { ...next, activeEventIds: [...next.activeEventIds, ...eligible] };
}

export function isGameOver(scenario: Scenario, state: ProjectState): boolean {
  return scenario.endWhen(state);
}

/** Skip forward through days with nothing eligible, stopping at the next decision or the end. */
export function advanceUntilActionable(scenario: Scenario, state: ProjectState): ProjectState {
  let next = state;
  while (!isGameOver(scenario, next) && next.activeEventIds.length === 0) {
    next = advanceDay(scenario, next);
  }
  return next;
}

export function resolveOutcome(scenario: Scenario, state: ProjectState) {
  return (
    scenario.outcomes.find((o) => o.when(state)) ?? scenario.outcomes[scenario.outcomes.length - 1]
  );
}
